import {
  AllProvidersModal,
  Div,
  H3,
  H5,
  Img,
  Modal,
  NumberType,
  P,
  Provider,
  Separator,
  VDriveProvider,
  formatAmount,
  lightTheme,
  providers,
  renderProviderIcon,
  renderProviderName,
  renderVDriveIcon,
  renderVDriveProviderName,
  vDriveProviders,
  isAdmin,
} from '@vgrubs/components';
import { Dispatch, SetStateAction } from 'react';

export const AllProvidersModalComponent = ({
  allProvidersModal,
  setAllProvidersModal,
  vDrive,
  customComponent,
}: {
  allProvidersModal: AllProvidersModal | null;
  setAllProvidersModal: Dispatch<SetStateAction<AllProvidersModal | null>>;
  vDrive?: boolean;
  customComponent?: React.ReactNode;
}) => {
  const filterProvider = (provider: Provider | VDriveProvider) => {
    return allProvidersModal?.byProvider?.find(
      (item) => item.provider === provider
    );
  };

  const allProviders = Object.values(
    vDrive
      ? (vDriveProviders as Record<string, VDriveProvider>)
      : (providers as Record<string, Provider>)
  )
    .filter((p) => {
      // only filter if the platform is NOT admin
      if (!isAdmin) {
        return p !== Provider.kanekt365 && p !== Provider.bytes;
      }
      return true;
    })
    .map((provider) => {
      return {
        provider,
        value: filterProvider(provider)?.amount || 0,
        amountToCompare: filterProvider(provider)?.amountToCompare || 0,
      };
    });

  function percentage(partialValue: number, totalValue: number) {
    return (100 * partialValue) / totalValue || 0;
  }

  return (
    <Modal
      isOpen={!!allProvidersModal}
      closeModal={() => setAllProvidersModal(null)}
      logo={false}
      backgroundColor={lightTheme.white}
      padding="24px"
      width="600px"
      titleWithExit={allProvidersModal?.label}
      titleWithTextComponent={customComponent}
    >
      {allProviders
        .sort((a, b) => b.value - a.value)
        ?.map(({ provider, value, amountToCompare }, i) => {
          return (
            <Div key={i}>
              <Div $flex $between $alignCenter $height="50px">
                <Div $flex $alignCenter $gap="16px" $gapSM="12px">
                  <Img
                    alt=""
                    src={
                      vDrive
                        ? renderVDriveIcon(provider)
                        : renderProviderIcon(provider)
                    }
                    $width="40px"
                    $widthSM="36px"
                  />
                  <H5 $m="0" $left>
                    {i + 1}.{' '}
                    {vDrive
                      ? renderVDriveProviderName(provider)
                      : renderProviderName(provider)}
                  </H5>
                </Div>

                <Div
                  $width="50%"
                  $widthSM="auto"
                  $flex
                  $gap="16px"
                  $gapSM="0"
                  $alignCenter
                  $columnSM
                  $justifyEnd
                  $alignEndSM
                >
                  {' '}
                  <Div $flex $column $width="50%" $widthSM="auto">
                    <H5 $m="0" $right $color={allProvidersModal?.color}>
                      {formatAmount(
                        value,
                        allProvidersModal?.currency || NumberType.NUMBER
                      )}
                    </H5>

                    <P $m="0" $right $color={lightTheme.lightGray}>
                      {percentage(
                        value,
                        allProvidersModal?.amount || 0
                      ).toFixed(0)}
                      %
                    </P>
                  </Div>
                  {allProvidersModal?.isComparing && (
                    <Div $flex $column $width="50%" $widthSM="auto">
                      <H5 $m="0" $right>
                        {formatAmount(
                          amountToCompare,
                          allProvidersModal?.currency || NumberType.NUMBER
                        )}
                      </H5>

                      <P $m="0" $right $color={lightTheme.lightGray}>
                        {percentage(
                          amountToCompare,
                          allProvidersModal?.amountToCompare || 0
                        ).toFixed(0)}
                        %
                      </P>
                    </Div>
                  )}
                </Div>
              </Div>

              <Separator $m="8px 0" $color={lightTheme.lightGray} />
            </Div>
          );
        })}
      <Div $flex $justifyEnd $width="100%">
        <Div
          $flex
          $gap="16px"
          $gapSM="0px"
          $width="50%"
          $widthSM="100%"
          $justifyEnd
          $columnSM
          $alignEndSM
        >
          <Div $width="100%">
            <H5 $right $m="0" $mt="10">
              Total:
            </H5>
            <H3 $right $mb="0" $color={allProvidersModal?.color}>
              {formatAmount(
                allProvidersModal?.amount || 0,
                allProvidersModal?.currency || NumberType.NUMBER
              )}
            </H3>
          </Div>

          {allProvidersModal?.isComparing && (
            <Div $width="100%">
              <H5 $right $m="0" $mt="10">
                Compared to:
              </H5>
              <H3 $right $mb="0">
                {formatAmount(
                  allProvidersModal?.amountToCompare || 0,
                  allProvidersModal?.currency || NumberType.NUMBER
                )}
              </H3>
            </Div>
          )}
        </Div>
      </Div>
    </Modal>
  );
};
